// Frontend access model — mirrors the backend RBAC. The backend guard is the
// real boundary; this gates the sidebar + action buttons + route navigation
// from the logged-in user's effective permission set (see usePermissions).

export const ADMIN_ROLES = ['owner', 'admin', 'manager'];
/** Mirrors the backend's TEACHING_ROLES constant (common/constants/roles.ts). */
export const TEACHING_ROLES = ['owner', 'admin', 'manager', 'teacher'];

export function isAdminRole(role?: string): boolean {
  return !!role && ADMIN_ROLES.includes(role);
}

export function isTeachingRole(role?: string): boolean {
  return !!role && TEACHING_ROLES.includes(role);
}
export const ROLE_LABELS: Record<string, string> = {  owner: 'Owner',  admin: 'Admin',  manager: 'Principal',  teacher: 'Teacher',  staff: 'Staff',  cashier: 'Cashier',  student: 'Student',  parent: 'Parent',};export function roleLabel(role: string | null | undefined): string {  if (!role) return '-';  return ROLE_LABELS[role] || role;}

/** Plan feature gate — separate axis from role/permission access, sourced
 * from the auth store's `schoolFeatures` (set at login/school-select time
 * from the tenant session response). UX-only, same as canAccessPath: the
 * backend's own `@RequiresPlanFeature()` guard is the real boundary. */
export function hasPlanFeature(features: string[], key: string): boolean {
  return features.includes(key);
}

/** Mirrors backend/src/common/constants/plan-features.constants.ts — every
 * feature key seedable on a plan or grantable as a per-school add-on. */
export const KNOWN_PLAN_FEATURES = [
  'attendance',
  'fees',
  'basic_reports',
  'sms_alerts',
  'exams',
  'parent_app',
  'online_payments',
  'library',
  'transport',
  'hostel',
  'advanced_reports',
  'api_access',
  'biometric_devices',
  'custom_domain',
  'sso',
  'dedicated_support',
  'lms',
  'school_core',
] as const;

export type PermAction = 'list' | 'create' | 'delete';

/** Permission-gated module path-prefixes (mirror of backend PERMISSION_MODULES keys).
 * '/lms' is deliberately NOT here — it's gated by a fixed role list + plan
 * feature (TEACHING_ROLES/hasPlanFeature), not per-role custom permission
 * grants. See canAccessPath's '/lms' special-case below. */
export const MODULE_KEYS = [
  '/setup/academic-years',
  '/setup/classes',
  '/setup/subjects',
  '/promotion',
  '/students',
  '/parents',
  '/staff',
  '/transfer-certificates',
  '/attendance',
  '/exams',
  '/report-cards',
  '/timetable',
  '/fees',
  '/visitors',
  '/visits',
  '/settings',
  '/roles',
];

/** Longest module key that prefixes the given pathname, or null. */
export function moduleForPath(pathname: string): string | null {
  let best: string | null = null;
  for (const k of MODULE_KEYS) {
    if (pathname === k || pathname.startsWith(k + '/')) {
      if (!best || k.length > best.length) best = k;
    }
  }
  return best;
}

export interface AccessCtx {
  isAdmin: boolean;
  permissions: Set<string>;
}

/** Can the user view/navigate this path? */
export function canAccessPath(ctx: AccessCtx, pathname: string): boolean {
  if (ctx.isAdmin) return true;
  if (pathname === '/' || pathname === '/dashboard' || pathname.startsWith('/dashboard/'))
    return true;
  // /lms is gated on its own axis (TEACHING_ROLES + plan feature, enforced
  // by LmsRoute in routes/index.tsx) rather than the permission-module
  // system — deferring to it here, instead of falling through to the
  // `!mod -> false` default below, is what lets a non-admin teacher (who
  // has no entry in MODULE_KEYS/permissions for '/lms') reach the route at
  // all; ProtectedRoute would otherwise block it before LmsRoute ever runs.
  if (pathname === '/lms' || pathname.startsWith('/lms/')) return true;
  const mod = moduleForPath(pathname);
  if (!mod) return false; // unknown / admin-only modules (library, reports…)
  return ctx.permissions.has(`${mod}:list`);
}

/** Can the user perform an action on a module? */
export function can(
  ctx: AccessCtx,
  moduleKey: string,
  action: PermAction,
): boolean {
  if (ctx.isAdmin) return true;
  return ctx.permissions.has(`${moduleKey}:${action}`);
}
